import { c as _c } from "react/compiler-runtime";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { sentryCaptureError } from "@utils/sentry-utils";
import { useEffect, useState } from "react";

/**
 * URL route params for the snapshot redirect check.
 * Optional since they may be undefined during initial render.
 */

/**
 * Checks if the `projectId` in the URL belongs to a snapshot rather than a project.
 * If so, redirects to the parent project's snapshots tab with the snapshot ID as a query param:
 * `/{companyId}/projects/{parentProjectId}/snapshots?snapshotId={snapshotId}`
 *
 * Skips the API check when the project is already in the Redux store
 * (e.g. user navigated from the projects list), since known projects can't be snapshots.
 */
export function useSnapshotRedirect(t0) {
  const $ = _c(14);
  const {
    companyId,
    projectId
  } = t0;
  const {
    navigateToSnapshot
  } = useAppNavigation();
  let t1;
  if ($[0] !== projectId) {
    t1 = getProjectByIdSelector(projectId);
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const existingProject = useAppSelector(t1);
  let t2;
  if ($[2] !== companyId || $[3] !== existingProject || $[4] !== projectId) {
    t2 = () => Boolean(companyId && projectId) && !existingProject;
    $[2] = companyId;
    $[3] = existingProject;
    $[4] = projectId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [isRedirecting, setIsRedirecting] = useState(t2);
  let t3;
  let t4;
  if ($[6] !== companyId || $[7] !== existingProject || $[8] !== navigateToSnapshot || $[9] !== projectId) {
    t3 = () => {
      if (!companyId || !projectId || existingProject) {
        setIsRedirecting(false);
        return;
      }
      let isCancelled = false;
      const checkIfSnapshot = async function checkIfSnapshot(safeCompanyId, safeProjectId) {
        ;
        try {
          const snapshotDetails = await faroCoreApiClient.projects.getSnapshotDetails({
            companyId: safeCompanyId,
            snapshotId: safeProjectId
          });
          if (isCancelled) {
            return;
          }
          if (!snapshotDetails.originalProjectId) {
            setIsRedirecting(false);
            return;
          }
          navigateToSnapshot({
            companyId: safeCompanyId,
            parentProjectId: snapshotDetails.originalProjectId,
            snapshotId: safeProjectId
          });
        } catch (t5) {
          const error = t5;
          if (isCancelled) {
            return;
          }
          sentryCaptureError({
            error,
            title: "Failed to verify if the project is a snapshot."
          });
          setIsRedirecting(false);
        }
      };
      checkIfSnapshot(companyId, projectId);
      return () => {
        isCancelled = true;
      };
    };
    t4 = [companyId, existingProject, navigateToSnapshot, projectId];
    $[6] = companyId;
    $[7] = existingProject;
    $[8] = navigateToSnapshot;
    $[9] = projectId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== isRedirecting) {
    t5 = {
      isRedirecting
    };
    $[12] = isRedirecting;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}